import { Box, Button, Stack, Text } from '@chakra-ui/react'
import type { RegistrationLocation } from '@ipc/shared'
import { useEffect, useRef, useState } from 'react'
import { api } from '~/api/client'
import { useOpenFaq, WhyLink } from '~/components/member/WhyLink'
import { useT } from '~/i18n/use-t'
import {
  deviceTimeZone,
  fmtAccuracy,
  gpsBodyOf,
  watchBestFix,
  type FixSample,
  type GeoError,
  type Watch,
} from '~/lib/geolocation'

export type DeviceAnswer = 'fix' | 'refused' | null

type Phase = 'idle' | 'finding' | 'sending' | 'done' | 'failed' | 'refused'

/** Opens the FAQ entry `locationAllow` (how to allow it, and how to turn on precise location). */
function HowToAllow() {
  const { t } = useT()
  const openFaq = useOpenFaq()
  return (
    <Button type='button' variant='outline' size='lg' minH='12' onClick={() => openFaq('locationAllow')}>
      {t('place.gpsHow')}
    </Button>
  )
}

const geo = () => (typeof navigator === 'undefined' ? undefined : navigator.geolocation)

/** The phone's system, for the precise-location hint (unknown shows both lines). */
function phoneOs(): 'ios' | 'android' | 'unknown' {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'unknown'
}

/**
 * S3 "Confirm your location" (location v2, ruling R111a): a watch that keeps the best fix with live progress, sent by
 * itself when it stops; the approximate-location hint; denied / unavailable per mode (`required`: the small link "I
 * can't share my location"; `strict`: no way past; `optional`: "Continue without it"). The member never sees how the
 * location compares with the place (R5).
 */
export function DeviceLocationCard({
  placeId,
  info,
  initial,
  onAnswer,
}: {
  placeId: number
  info: RegistrationLocation
  initial: DeviceAnswer
  onAnswer: (a: DeviceAnswer) => void
}) {
  const { t } = useT()
  const [phase, setPhase] = useState<Phase>(initial === 'fix' ? 'done' : initial === 'refused' ? 'refused' : 'idle')
  const [progress, setProgress] = useState<number | null>(null)
  const [accuracy, setAccuracy] = useState<number | null>(null)
  const [error, setError] = useState<GeoError | 'send' | null>(null)
  const watch = useRef<Watch | null>(null)
  const alive = useRef(true)

  // Gate audit 3: on unmount (or a new place, which remounts the card) the running watch is dropped, so its result
  // is never POSTed for the old place and no state is set on an unmounted card.
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      const w = watch.current
      watch.current = null
      w?.stop()
    }
  }, [])

  const share = async () => {
    watch.current?.stop()
    setPhase('finding')
    setProgress(null)
    setError(null)
    onAnswer(null)
    const w = watchBestFix(geo(), { onProgress: (b: FixSample | null) => setProgress(b ? b.accuracyM : null) })
    watch.current = w
    const r = await w.promise
    if (watch.current !== w || !alive.current) return
    if (!r.best) {
      setError(r.error)
      setPhase('failed')
      return
    }
    setPhase('sending')
    try {
      await api('registrationGps', { body: gpsBodyOf({ ...r, best: r.best }, placeId, geo()) })
      if (!alive.current) return
      setAccuracy(r.best.accuracyM)
      setPhase('done')
      onAnswer('fix')
    } catch {
      if (!alive.current) return
      setError('send')
      setPhase('failed')
    }
  }

  const refuse = async () => {
    watch.current?.stop()
    watch.current = null
    const reason = error && error !== 'send' ? error : 'skipped'
    try {
      const tz = deviceTimeZone()
      await api('registrationGpsRefused', { body: { reason, ...(tz ? { timeZone: tz } : {}) } })
      if (!alive.current) return
      setPhase('refused')
      onAnswer('refused')
    } catch {
      if (!alive.current) return
      setError('send')
      setPhase('failed')
    }
  }

  const busy = phase === 'finding' || phase === 'sending'
  const approximate = phase === 'done' && accuracy !== null && accuracy > info.hintAccuracyM
  const answered = phase === 'done' || phase === 'refused'
  const live = busy || phase === 'done' || phase === 'failed' || phase === 'refused'
  const os = phoneOs()

  return (
    // On a phone no card (its frame costs a line of every sentence): a hairline above makes it a section of the
    // step. From `md` the bordered card.
    <Stack
      gap={2}
      borderColor='border'
      borderTopWidth='1px'
      borderInlineWidth={{ base: 0, md: '1px' }}
      borderBottomWidth={{ base: 0, md: '1px' }}
      borderRadius={{ base: 0, md: 'lg' }}
      bg={{ base: 'transparent', md: 'bg' }}
      pt={{ base: 2, md: 5 }}
      px={{ base: 0, md: 5 }}
      pb={{ base: 0, md: 5 }}
      data-testid='device-location'
    >
      <Text fontWeight='bold'>{t(info.mode === 'optional' ? 'place.locTitleOptional' : 'place.locTitle')}</Text>
      <Text>{t('place.locWhy')}</Text>
      {/* `required`: the way out ("I can't share my location") ends the sentence that says what it leads to. */}
      {info.mode === 'required' && (
        <Text>
          {t('place.locRequired')}
          {!answered && (
            <>
              {' '}
              <WhyLink label={t('place.locCant')} color='fg' inline onClick={() => void refuse()} />
            </>
          )}
        </Text>
      )}
      {info.mode === 'strict' && <Text>{t('place.locStrict')}</Text>}

      {/* The live region is always there (a region that appears with its text is not reliably read); while empty
          it cancels the gap it would add. */}
      <Box aria-live='polite' mt={live ? 0 : -2}>
        {busy && (
          <Text role='status'>
            {progress === null
              ? t('place.locFinding')
              : t('place.locFindingAccuracy', { accuracy: fmtAccuracy(progress) })}
          </Text>
        )}
        {phase === 'done' && !approximate && <Text role='status'>{t('place.locDone')}</Text>}
        {approximate && (
          <Stack gap={2}>
            <Text role='status'>{t('place.locApprox', { accuracy: fmtAccuracy(accuracy!) })}</Text>
            {/* Gate audit 7: only the line of this phone's system (both when unknown); the FAQ has both. */}
            {os !== 'android' && <Text>{t('place.locApproxIphone')}</Text>}
            {os !== 'ios' && <Text>{t('place.locApproxAndroid')}</Text>}
          </Stack>
        )}
        {phase === 'failed' && (
          <Stack gap={2} align='flex-start'>
            <Text role='alert'>
              {error === 'denied'
                ? t('place.locBlocked')
                : error === 'unsupported'
                  ? t('place.locUnsupported')
                  : error === 'send'
                    ? t('common.somethingWrong')
                    : t('place.locNotFound')}
            </Text>
            {(error === 'denied' || error === 'unavailable') && <HowToAllow />}
          </Stack>
        )}
        {phase === 'refused' && (
          <Text role='status'>{t(info.mode === 'optional' ? 'place.locSkipped' : 'place.locCantDone')}</Text>
        )}
      </Box>

      {/* Always a way to share again (gate audit 1): "Try again" stays after a fix or a refusal. */}
      <Button type='button' size='lg' minH='12' w='full' variant='outline' loading={busy} onClick={() => void share()}>
        {phase === 'idle' || phase === 'refused' ? t('place.locShare') : t('place.locTryAgain')}
      </Button>
      {info.mode === 'optional' && !answered && (
        <Button type='button' variant='outline' size='lg' minH='12' w='full' onClick={() => void refuse()}>
          {t('place.locSkip')}
        </Button>
      )}
    </Stack>
  )
}
